import {
  type AddressType,
  type GeocodingAddressComponentType,
  type GeocodingResult,
} from '@google/maps'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Combobox } from '@headlessui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from 'tailwind-v4/lib/utils'
import { toast } from 'sonner'
import { type VenueGeoSettingsType } from '@/state/morpheus/types/venue'
import { GeoCodingService } from '@/utils/maps'
import useDebounce from '@/utils/helpers/debounce'

interface PassedProps {
  onAddressSelect?: (address: VenueGeoSettingsType) => void
  address: VenueGeoSettingsType
  placeholder?: string
  helperText?: string
  inputClassName?: string
}

type Props = PassedProps

const service = new GeoCodingService()

function GoogleAddressSelect({
  onAddressSelect,
  address: propsAddress,
  placeholder,
  helperText = 'Search for your venue as customers would on Google (include business name and location).',
  inputClassName,
}: Props) {
  const [googleAddresses, setGoogleAddresses] = useState<GeocodingResult[]>([])
  const [googleAddressesLoading, setGoogleAddressesLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState(
    propsAddress?.formattedAddress ?? ''
  )
  const [error, setError] = useState<string | null>(null)
  const deferedSearchTerm = useDebounce(searchTerm, 500)

  useEffect(() => {
    setSearchTerm(propsAddress?.formattedAddress ?? '')
  }, [propsAddress])

  const fetchAddressViaPostcode = useCallback(async (address: string) => {
    if (address.length < 2) {
      setGoogleAddresses([])
      return
    }

    setGoogleAddressesLoading(true)
    setError(null)

    try {
      const response = await service.geocodeAddress(address)
      setGoogleAddresses(response)

      if (response.length === 0) {
        setError('No addresses found. Try being more specific.')
      }
    } catch (err) {
      setError('Failed to search addresses. Please try again.')
      toast.error('Failed to search addresses')
    } finally {
      setGoogleAddressesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!deferedSearchTerm) return
    fetchAddressViaPostcode(deferedSearchTerm)
  }, [deferedSearchTerm, fetchAddressViaPostcode])

  const googleAddressBuilder = (
    key: AddressType | GeocodingAddressComponentType,
    addressObject: GeocodingResult
  ) => {
    const find = addressObject.address_components.find(
      (addressComponent: any) => addressComponent.types.includes(key)
    )

    if (!find) {
      return null
    }

    return find.long_name
  }

  const formatAddressOption = (address: GeocodingResult) => {
    const street = googleAddressBuilder('route', address)
    const number = googleAddressBuilder('street_number' as AddressType, address)
    const city =
      googleAddressBuilder('locality', address) ||
      googleAddressBuilder('postal_town', address)
    const postcode = googleAddressBuilder('postal_code', address)

    const parts = [number, street, city, postcode].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : address.formatted_address
  }

  const [selectedAddress, setSelectedAddress] =
    useState<GeocodingResult | null>(null)

  const handleSelect = (address: GeocodingResult | null) => {
    if (!address) return

    const { geometry, formatted_address, place_id } = address
    setSelectedAddress(address)
    setSearchTerm(formatted_address)

    if (!onAddressSelect) return
    onAddressSelect({
      postCode: googleAddressBuilder('postal_code', address),
      route: googleAddressBuilder('route', address),
      postalTown: googleAddressBuilder('postal_town', address),
      administrativeAreaLevel2: googleAddressBuilder(
        'administrative_area_level_2',
        address
      ),
      administrativeAreaLevel1: googleAddressBuilder(
        'administrative_area_level_1',
        address
      ),
      country: googleAddressBuilder('country', address),
      latitude: geometry.location.lat,
      longitude: geometry.location.lng,
      formattedAddress: formatted_address,
      googlePlaceId: place_id,
    })
  }

  const hasAddress =
    Boolean(selectedAddress) || Boolean(propsAddress?.formattedAddress)
  const showStatus = hasAddress || searchTerm.length > 1
  let statusChip: ReactNode = null
  if (hasAddress) {
    statusChip = (
      <span className="rounded-md border border-success/25 bg-success/5 px-2 py-0.5 text-xs font-medium text-success">
        {selectedAddress ? 'Selected' : 'Current'}
      </span>
    )
  } else if (searchTerm.length > 1 && !googleAddressesLoading) {
    statusChip = (
      <span className="rounded-md border border-warning/25 bg-warning/5 px-2 py-0.5 text-xs font-medium text-warning">
        Not selected
      </span>
    )
  }

  return (
    <div className="relative flex flex-col gap-2">
      <Combobox as="div" value={selectedAddress} onChange={handleSelect}>
        <div className="relative">
          <Combobox.Input
            className={cn(
              'flex h-10 w-full rounded-lg border border-border bg-transparent px-3 py-2 pr-10 text-base transition-colors outline-none placeholder:text-foreground/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 md:text-sm',
              inputClassName
            )}
            displayValue={(address: GeocodingResult | null) =>
              address?.formatted_address || searchTerm
            }
            onChange={(event: any) => {
              setSearchTerm(event.target.value)
            }}
            placeholder={
              placeholder ||
              propsAddress?.formattedAddress ||
              "e.g. 'The Grand Hotel London' or 'Acme Restaurant Manchester'"
            }
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
            <ChevronsUpDown
              className="size-4 text-muted-foreground"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-popover py-1 text-sm text-popover-foreground shadow-md focus:outline-none">
          {googleAddressesLoading ? (
            <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">
              Loading addresses…
            </div>
          ) : null}

          {!googleAddressesLoading &&
            googleAddresses.length === 0 &&
            searchTerm.length > 1 && (
              <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">
                {error ?? 'No addresses found'}
              </div>
            )}

          {googleAddresses.map((address: any) => (
            <Combobox.Option
              key={address.place_id}
              className={({ active }) =>
                cn(
                  'relative cursor-default select-none py-2 pl-10 pr-4',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'text-popover-foreground'
                )
              }
              value={address}
            >
              {({ selected, active }) => (
                <span>
                  <span
                    className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                  >
                    {formatAddressOption(address)}
                  </span>
                  {selected ? (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-foreground">
                      <Check className="size-4" aria-hidden="true" />
                    </span>
                  ) : null}
                </span>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox>

      {helperText ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}

      {showStatus ? (
        <div className="flex items-center gap-2 text-sm">
          {statusChip}
          <span className="text-muted-foreground">
            {selectedAddress?.formatted_address ??
              propsAddress?.formattedAddress ??
              'Please select an address'}
          </span>
        </div>
      ) : null}
    </div>
  )
}

export default GoogleAddressSelect
